import React, { useState, useEffect, useMemo } from 'react';
import { XMarkIcon, ArrowRightIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface SourceValue {
  value: string;
  count: number;
  mappedTo?: string;
}

interface PicklistOption {
  id: string;
  externalCode: string;
  status: string;
  picklistLabels: Array<{
    locale: string;
    label: string;
  }>;
}

interface ValueMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (mappings: { [sourceValue: string]: string }) => void;
  entityName: string;
  fieldName: string;
  fieldLabel: string;
  sourceColumn: string;
  sourceValues: SourceValue[];
  picklistOptions: PicklistOption[];
  picklistId?: string;
  existingMappings?: { [sourceValue: string]: string };
}

const ValueMappingModal: React.FC<ValueMappingModalProps> = ({
  isOpen,
  onClose,
  onSave,
  entityName,
  fieldName,
  fieldLabel,
  sourceColumn,
  sourceValues,
  picklistOptions,
  picklistId,
  existingMappings = {}
}) => {
  const [mappings, setMappings] = useState<{ [sourceValue: string]: string }>(existingMappings);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSourceValue, setSelectedSourceValue] = useState<string | null>(null);

  // Helper to get primary label for picklist option
  const getPicklistLabel = (option: PicklistOption) => {
    const enLabel = option.picklistLabels?.find(l => l.locale === 'en' || l.locale === 'en_US');
    const firstLabel = option.picklistLabels?.[0];
    return enLabel?.label || firstLabel?.label || option.externalCode;
  };

  // Filter active picklist options
  const activePicklistOptions = useMemo(() => {
    if (!Array.isArray(picklistOptions)) {
      console.warn('ValueMappingModal: picklistOptions is not an array', picklistOptions);
      return [];
    }
    return picklistOptions.filter(option => option.status === 'ACTIVE');
  }, [picklistOptions]);

  // Filter picklist options based on search
  const filteredPicklistOptions = useMemo(() => {
    if (!searchTerm) return activePicklistOptions;
    return activePicklistOptions.filter(option => {
      const label = getPicklistLabel(option);
      return label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        option.externalCode.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [activePicklistOptions, searchTerm]);

  // Statistics
  const stats = useMemo(() => {
    const totalSourceValues = sourceValues.length;
    const mappedValues = Object.keys(mappings).length;
    const unmappedValues = sourceValues.filter(sv => !mappings[sv.value]).length;
    const totalRecords = sourceValues.reduce((sum, sv) => sum + sv.count, 0);
    const mappedRecords = sourceValues
      .filter(sv => mappings[sv.value])
      .reduce((sum, sv) => sum + sv.count, 0);

    return {
      totalSourceValues,
      mappedValues,
      unmappedValues,
      totalRecords,
      mappedRecords,
      completionPercentage: totalSourceValues > 0 ? Math.round((mappedValues / totalSourceValues) * 100) : 0
    };
  }, [sourceValues, mappings]);

  // Reset mappings when modal opens
  useEffect(() => {
    if (isOpen) {
      setMappings(existingMappings);
      setSearchTerm('');
      setSelectedSourceValue(null);
    }
  }, [isOpen, existingMappings]);

  const handleMapping = (sourceValue: string, targetValue: string) => {
    setMappings(prev => ({
      ...prev,
      [sourceValue]: targetValue
    }));
  };

  const handleRemoveMapping = (sourceValue: string) => {
    setMappings(prev => {
      const newMappings = { ...prev };
      delete newMappings[sourceValue];
      return newMappings;
    });
  };

  const handleSave = () => {
    onSave(mappings);
    toast.success(`Value mappings saved for ${fieldLabel}`);
    onClose();
  };

  const handleAutoMap = () => {
    const newMappings = { ...mappings };
    let autoMappedCount = 0;

    sourceValues.forEach(sourceValue => {
      if (!newMappings[sourceValue.value]) {
        // Try to find exact match first
        const exactMatch = activePicklistOptions.find(option => {
          const label = getPicklistLabel(option);
          return label.toLowerCase() === sourceValue.value.toLowerCase() ||
            option.externalCode.toLowerCase() === sourceValue.value.toLowerCase();
        });

        if (exactMatch) {
          newMappings[sourceValue.value] = exactMatch.externalCode;
          autoMappedCount++;
        } else {
          // Try partial match
          const partialMatch = activePicklistOptions.find(option => {
            const label = getPicklistLabel(option);
            return label.toLowerCase().includes(sourceValue.value.toLowerCase()) ||
              sourceValue.value.toLowerCase().includes(label.toLowerCase());
          });

          if (partialMatch) {
            newMappings[sourceValue.value] = partialMatch.externalCode;
            autoMappedCount++;
          }
        }
      }
    });

    setMappings(newMappings);
    if (autoMappedCount > 0) {
      toast.success(`Auto-mapped ${autoMappedCount} values`);
    } else {
      toast('No automatic mappings found', { icon: 'ℹ️' });
    }
  };

  const handleClearAll = () => {
    setMappings({});
    toast('All mappings cleared', { icon: '🗑️' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Value Mapping</h2>
              <p className="text-sm text-gray-600">
                Map values from <strong>{sourceColumn}</strong> to <strong>{fieldLabel}</strong> options
              </p>
              {picklistId && (
                <p className="text-xs text-gray-500">Picklist ID: {picklistId}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Statistics */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.totalSourceValues}</div>
              <div className="text-xs text-gray-500">Source Values</div>
            </div>
            <div className="bg-white rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{stats.mappedValues}</div>
              <div className="text-xs text-gray-500">Mapped</div>
            </div>
            <div className="bg-white rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-red-600">{stats.unmappedValues}</div>
              <div className="text-xs text-gray-500">Unmapped</div>
            </div>
            <div className="bg-white rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.completionPercentage}%</div>
              <div className="text-xs text-gray-500">Complete</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={handleAutoMap}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              Auto Map
            </button>
            <button
              onClick={handleClearAll}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
            >
              Clear All
            </button>
            <div className="flex-1"></div>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium"
            >
              Save Mappings ({stats.mappedValues})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex h-[600px]">
          {/* Source Values Panel */}
          <div className="w-1/2 border-r border-gray-200 flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="font-medium text-gray-900">Source Values ({sourceColumn})</h3>
              <p className="text-sm text-gray-500">Click a value to select target mapping</p>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-2">
                {sourceValues.map((sourceValue) => {
                  const mappedTo = mappings[sourceValue.value];
                  const isSelected = selectedSourceValue === sourceValue.value;

                  return (
                    <div
                      key={sourceValue.value}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : mappedTo
                            ? 'border-green-200 bg-green-50'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                      onClick={() => setSelectedSourceValue(sourceValue.value)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{sourceValue.value}</div>
                          <div className="text-sm text-gray-500">{sourceValue.count} records</div>
                        </div>
                        {mappedTo && (
                          <div className="flex items-center gap-2">
                            <ArrowRightIcon className="w-4 h-4 text-gray-400" />
                            <div className="text-sm font-medium text-green-600">{mappedTo}</div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveMapping(sourceValue.value);
                              }}
                              className="text-red-500 hover:text-red-700 p-1"
                              title="Remove mapping"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        {isSelected && !mappedTo && (
                          <div className="text-blue-600">
                            <ArrowRightIcon className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Target Options Panel */}
          <div className="w-1/2 flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="font-medium text-gray-900">Target Options ({fieldLabel})</h3>
              <input
                type="text"
                placeholder="Search options..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {selectedSourceValue ? (
                <div className="space-y-2">
                  <div className="text-sm text-gray-600 mb-3">
                    Select target value for: <strong>{selectedSourceValue}</strong>
                  </div>
                  {filteredPicklistOptions.map((option) => {
                    const label = getPicklistLabel(option);
                    const isCurrentMapping = mappings[selectedSourceValue] === option.externalCode;

                    return (
                      <div
                        key={option.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${isCurrentMapping
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 bg-white hover:bg-blue-50'
                          }`}
                        onClick={() => handleMapping(selectedSourceValue, option.externalCode)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">{label}</div>
                            <div className="text-sm text-gray-500">{option.externalCode}</div>
                          </div>
                          {isCurrentMapping && (
                            <CheckCircleIcon className="w-5 h-5 text-green-600" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {filteredPicklistOptions.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <ExclamationTriangleIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No matching options found</p>
                      <p className="text-sm">Try adjusting your search term</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <ArrowRightIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Select a source value to map</p>
                    <p className="text-sm">Choose a value from the left panel</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {stats.mappedRecords} of {stats.totalRecords} records will be mapped ({Math.round((stats.mappedRecords / stats.totalRecords) * 100)}%)
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={stats.mappedValues === 0}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save {stats.mappedValues} Mappings
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValueMappingModal;